'use client';

import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cn } from './cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner in place of `startIcon` and disables interaction. */
  loading?: boolean;
  /** Icon at the reading start (right in Hebrew, left in English). */
  startIcon?: React.ReactNode;
  /** Icon at the reading end. */
  endIcon?: React.ReactNode;
  /** Stretch to the container width — the default for mobile sheets. */
  block?: boolean;
  /** Render as the single child element (links styled as buttons). */
  asChild?: boolean;
}

/** Shared with IconButton so the two never drift. */
export const BUTTON_BASE = cn(
  'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
  'rounded-md font-medium leading-none',
  'transition-[background-color,color,box-shadow,border-color,transform] duration-(--duration-fast) ease-out-soft',
  'active:scale-[0.985]',
  'disabled:pointer-events-none disabled:opacity-45',
  'aria-disabled:pointer-events-none aria-disabled:opacity-45',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
);

export const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-accent text-accent-fg shadow-xs',
    'hover:bg-accent-hover active:bg-accent-active',
  ),
  secondary: cn(
    'border border-line-strong bg-surface text-ink shadow-xs',
    'hover:bg-paper-2 hover:border-line-strong active:bg-paper-3',
  ),
  ghost: cn('text-ink-2', 'hover:bg-paper-3 hover:text-ink active:bg-paper-3'),
  danger: cn('bg-danger text-danger-fg shadow-xs', 'hover:bg-danger-hover active:bg-danger-hover'),
};

/** Height / padding / type scale. `md` is the 44 px touch target. */
export const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: 'k-hit h-9 px-3 text-sm [&_svg]:size-4',
  md: 'h-touch px-4 text-sm [&_svg]:size-[1.125rem]',
  lg: 'h-13 px-6 text-base [&_svg]:size-5',
};

const SPINNER_FOR_SIZE = { sm: 'xs', md: 'sm', lg: 'sm' } as const;

/**
 * The primary action control.
 *
 * - Icons use logical order: `startIcon` sits on the right in Hebrew, left in English.
 * - `loading` keeps the label so the button never changes width mid-request.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    startIcon,
    endIcon,
    block,
    asChild,
    className,
    children,
    disabled,
    type,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      data-variant={variant}
      className={cn(
        BUTTON_BASE,
        BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        block && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size={SPINNER_FOR_SIZE[size]} /> : startIcon}
      <Slottable>{children}</Slottable>
      {endIcon}
    </Comp>
  );
});
